import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  autonomousOpsApi,
  type ApprovalRequest,
  type AutomationRule,
  type EscalationEntry,
  type ExecutionHistoryEntry,
  type OperationalTask,
  type OperationsDashboardSnapshot,
  type ReminderEntry,
  type TaskAssignment,
  type WorkflowAnalyticsSnapshot,
  type WorkflowInstance,
} from '../../api/autonomous-ops-aow001-runtime';

type Tab = 'dashboard' | 'tasks' | 'workflows' | 'approvals' | 'escalations' | 'automation' | 'analytics' | 'history';

export function AutonomousOperationsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<OperationsDashboardSnapshot | null>(null);
  const [tasks, setTasks] = useState<OperationalTask[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowInstance[]>([]);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [assignments, setAssignments] = useState<TaskAssignment[]>([]);
  const [reminders, setReminders] = useState<ReminderEntry[]>([]);
  const [escalations, setEscalations] = useState<EscalationEntry[]>([]);
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [analytics, setAnalytics] = useState<WorkflowAnalyticsSnapshot | null>(null);
  const [history, setHistory] = useState<ExecutionHistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-teal-200 bg-white dark:border-teal-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-teal-700 dark:text-teal-300';
  const shell = 'bg-teal-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, t, wf, appr, assign, rem, esc, auto, anal, hist] = await Promise.all([
        autonomousOpsApi.dashboard(), autonomousOpsApi.tasks(), autonomousOpsApi.workflows(),
        autonomousOpsApi.approvals(), autonomousOpsApi.assignments(), autonomousOpsApi.reminders(),
        autonomousOpsApi.escalations(), autonomousOpsApi.automation(), autonomousOpsApi.analytics(),
        autonomousOpsApi.history(),
      ]);
      setDashboard(dash); setTasks(t); setWorkflows(wf); setApprovals(appr);
      setAssignments(assign); setReminders(rem); setEscalations(esc); setRules(auto);
      setAnalytics(anal); setHistory(hist);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Autonomous Operations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Overview' }, { id: 'tasks', label: 'My Tasks' },
    { id: 'workflows', label: 'Workflows' }, { id: 'approvals', label: 'Approvals' },
    { id: 'escalations', label: 'Escalations' }, { id: 'automation', label: 'Automation' },
    { id: 'analytics', label: 'Analytics' }, { id: 'history', label: 'Execution Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>AOW-000001 · Autonomous Operations & Workflow Engine</p>
            <h1 className="text-xl font-semibold">Execution Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Active Tasks <strong className={accent}>{dashboard.active_tasks}</strong></span>
                <span>Pending Approvals <strong className={accent}>{dashboard.pending_approvals}</strong></span>
                <span>Completion <strong className={accent}>{dashboard.completion_rate_pct}%</strong></span>
                <span>Impact <strong className={accent}>{dashboard.business_impact_usd}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/intelligence-qa" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Intelligence QA</Link>
            <Link to="/platform/autonomous-ops-aow001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading execution platform…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-teal-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Active Tasks', value: dashboard.active_tasks },
            { label: 'Running Workflows', value: dashboard.running_workflows },
            { label: 'Automation Rate', value: `${dashboard.automation_rate_pct}%` },
            { label: 'Time Saved', value: `${dashboard.time_saved_hours}h` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'tasks' && tasks.map((task) => (
        <div key={task.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between">
            <p className="font-semibold">{task.title}</p>
            <span className={`text-sm uppercase ${task.status === 'completed' ? 'text-teal-600' : task.priority === 'critical' ? 'text-red-600' : muted}`}>{task.status}</span>
          </div>
          <p className={`text-xs ${muted}`}>{task.type.replace(/_/g, ' ')} · {task.assignee ?? 'unassigned'} · {task.priority}</p>
        </div>
      ))}

      {tab === 'workflows' && workflows.map((wf) => (
        <div key={wf.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{wf.name}</p><span className="text-xs uppercase">{wf.status}</span></div>
          <p className={`text-sm ${accent}`}>{wf.steps_done}/{wf.steps_total} steps · {wf.template}</p>
        </div>
      ))}

      {tab === 'approvals' && approvals.map((a) => (
        <div key={a.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{a.title}</p><span className={`text-xs uppercase ${a.status === 'approved' ? 'text-teal-600' : 'text-amber-600'}`}>{a.status}</span></div>
          <p className={`text-xs ${muted}`}>{a.approval_type} · {a.requested_by}{a.amount_usd ? ` · ${a.amount_usd}` : ''}</p>
        </div>
      ))}

      {tab === 'escalations' && escalations.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold">{e.reason}</p><span className={`text-xs uppercase ${e.severity === 'critical' ? 'text-red-600' : muted}`}>{e.severity}</span></div>
          <p className={`text-xs ${muted}`}>Task {e.task_id} · {e.status}</p>
        </div>
      ))}

      {tab === 'automation' && rules.map((r) => (
        <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
          <p className="font-semibold">{r.name}</p>
          <p className={`text-sm ${muted}`}>When {r.trigger} · If {r.condition} · Then {r.action}</p>
          <p className={`text-xs ${r.enabled ? 'text-teal-600' : muted}`}>{r.enabled ? 'Enabled' : 'Disabled'}</p>
        </div>
      ))}

      {tab === 'analytics' && analytics && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Completion Rate', value: `${analytics.completion_rate_pct}%` },
            { label: 'Avg Resolution', value: `${analytics.avg_resolution_hours}h` },
            { label: 'Execution Success', value: `${analytics.execution_success_pct}%` },
            { label: 'Automation Rate', value: `${analytics.automation_rate_pct}%` },
            { label: 'Avg Approval Time', value: `${analytics.avg_approval_hours}h` },
            { label: 'Business Impact', value: analytics.business_impact_usd },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'history' && history.map((e) => (
        <div key={e.id} className={`rounded-xl border p-4 ${card}`}>
          <div className="flex justify-between"><p className="font-semibold capitalize">{e.action} — {e.entity_type}</p><span className={`text-xs ${muted}`}>{e.actor}</span></div>
          <p className={`text-sm ${muted}`}>{e.before && e.after ? `${e.before} → ${e.after}` : e.outcome ?? ''}{e.business_impact ? ` · ${e.business_impact}` : ''}</p>
          <p className={`text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</p>
        </div>
      ))}

      {tab === 'dashboard' && reminders.length > 0 && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h2 className="font-semibold">Upcoming Reminders</h2>
          {reminders.map((r) => (
            <p key={r.id} className={`text-sm ${muted}`}>{r.message}</p>
          ))}
        </div>
      )}

      {tab === 'dashboard' && assignments.length > 0 && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h2 className="font-semibold">Recent Assignments</h2>
          {assignments.map((a) => (
            <p key={a.task_id} className={`text-sm ${muted}`}>{a.task_id} → {a.assignee} ({a.assign_type})</p>
          ))}
        </div>
      )}
    </div>
  );
}
